import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import BorderedSelect from 'src/components/form-new/bordered-select';

/** Sentinel for line items with no platform (BorderedSelect treats "" as no selection). */
export const BULK_APPROVE_EMPTY_PLATFORM_VALUE = '__billing_platform_none__';

export type BulkApprovePlatformOption = {
  value: string;
  label: string;
};

interface BulkApproveByPlatformModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  options: BulkApprovePlatformOption[];
  onSubmit: (platformKey: string) => Promise<void>;
}

const BulkApproveByPlatformModal: React.FC<BulkApproveByPlatformModalProps> = ({
  open,
  onOpenChange,
  options,
  onSubmit,
}) => {
  const [selected, setSelected] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected('');
    }
  }, [open]);

  const handleSubmit = async() => {
    if (!selected || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit(selected);
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = Boolean(selected) && options.length > 0 && !isSubmitting;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className="bg-white sm:max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="text-gray-900">
            Bulk approve
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Choose a platform to approve every pending line item on this invoice that belongs to that platform.
            Already approved or archived lines are skipped.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <BorderedSelect
            label="Platform"
            searchEnabled
            fullWidth
            containerMaxWidth="100%"
            placeholder="Select a platform"
            disabled={options.length === 0}
            options={options.map((o) => ({ value: o.value, label: o.label }))}
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          />
          {options.length === 0 ? (
            <p className="mt-2 text-sm text-amber-700">
              There are no pending line items to approve by platform on this invoice.
            </p>
          ) : null}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            className="rounded-full border-gray-300"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            className="rounded-full"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
          >
            {isSubmitting ? 'Approving…' : 'Approve'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkApproveByPlatformModal;
